/**
 * v6.32.115 — Super Admin → Demo Accounts tab.
 *
 * Lists every managed demo / ERMA-auditor account and lets the
 * super-admin disable / enable / revoke / reset-password / log-resend
 * with a one-click control row.
 *
 * Server enforces is_demo=true OR ERMA-allowlist on every mutating call,
 * so a buggy click here cannot leak into a real customer record.
 */
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  KeyRound, Ban, Power, Mail, RotateCcw, ShieldAlert,
  Search, ClipboardList,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-300",
  disabled: "bg-amber-100 text-amber-700 border-amber-300",
  revoked: "bg-rose-100 text-rose-700 border-rose-300",
};

const DemoAccountsTab = () => {
  const [accounts, setAccounts] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [busyUid, setBusyUid] = useState(null);
  const [revealedPassword, setRevealedPassword] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, l] = await Promise.all([
        axios.get(`${API}/superadmin/demo-accounts`),
        axios.get(`${API}/superadmin/demo-accounts/audit?limit=50`),
      ]);
      setAccounts(a.data.accounts || []);
      setAudit(l.data.rows || []);
    } catch (e) {
      toast.error("Failed to load demo accounts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const action = async (path, uid, label) => {
    if (busyUid) return;
    setBusyUid(uid);
    try {
      const res = await axios.post(`${API}/superadmin/demo-accounts/${path}`, { user_id: uid });
      if (path === "reset-password" && res.data?.new_password) {
        setRevealedPassword({
          email: res.data.email,
          password: res.data.new_password,
          shownAt: Date.now(),
        });
      }
      toast.success(`${label} OK`);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "action failed";
      toast.error(`${label}: ${msg}`);
    } finally {
      setBusyUid(null);
    }
  };

  const resetData = async () => {
    if (!window.confirm("Wipe all demo exam_sessions + responses + results for the sales tenant? Real customer data is NEVER touched.")) return;
    try {
      const r = await axios.post(`${API}/superadmin/demo-accounts/reset-data`);
      toast.success(`Reset: ${r.data.deleted_sessions} sessions, ${r.data.deleted_results} results.`);
      await load();
    } catch (e) {
      toast.error("Reset failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return accounts;
    return accounts.filter((a) =>
      (a.email || "").toLowerCase().includes(f) ||
      (a.name || "").toLowerCase().includes(f) ||
      (a.role || "").toLowerCase().includes(f)
    );
  }, [accounts, filter]);

  return (
    <div className="space-y-5" data-testid="demo-accounts-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <ShieldAlert size={22} className="text-amber-600" />
            Demo &amp; ERMA-Auditor Accounts
          </h2>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Manage sealed sales-demo accounts and the ERMA auditor account.
            Every mutation is server-enforced to <code className="text-xs bg-gray-100 px-1">is_demo=true</code> or the
            ERMA allowlist — real customer accounts <strong>cannot</strong> be touched here.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} className="btn-sharp h-9" data-testid="demo-accounts-refresh">
            <RotateCcw size={14} className="mr-1" /> Refresh
          </Button>
          <Button variant="outline" onClick={resetData} className="btn-sharp h-9 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="demo-accounts-reset-data">
            <Ban size={14} className="mr-1" /> Reset demo data
          </Button>
        </div>
      </div>

      {revealedPassword && (
        <div className="border-2 border-amber-300 bg-amber-50 p-4 rounded-md flex items-start justify-between gap-3" data-testid="demo-accounts-revealed-password">
          <div className="text-sm">
            <div className="font-bold text-amber-800 mb-1 flex items-center gap-1.5">
              <KeyRound size={14} /> New password (shown once — copy it now)
            </div>
            <div className="text-xs text-amber-700 mb-1">{revealedPassword.email}</div>
            <code className="font-mono text-base bg-white border border-amber-300 px-3 py-1.5">{revealedPassword.password}</code>
          </div>
          <Button size="sm" variant="ghost" onClick={() => setRevealedPassword(null)} className="text-amber-700">Dismiss</Button>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          placeholder="Search by email, name, role…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="pl-9 h-9"
          data-testid="demo-accounts-search"
        />
      </div>

      {/* Table */}
      <div className="border-2 border-gray-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="text-left px-3 py-2 text-xs font-semibold uppercase text-gray-500">Email</th>
              <th className="text-left px-3 py-2 text-xs font-semibold uppercase text-gray-500">Name</th>
              <th className="text-left px-3 py-2 text-xs font-semibold uppercase text-gray-500">Role</th>
              <th className="text-left px-3 py-2 text-xs font-semibold uppercase text-gray-500">Status</th>
              <th className="text-left px-3 py-2 text-xs font-semibold uppercase text-gray-500">Last login</th>
              <th className="text-right px-3 py-2 text-xs font-semibold uppercase text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-6 text-gray-400">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-6 text-gray-400">No managed demo accounts match.</td></tr>
            ) : filtered.map((a) => {
              const statusClass = STATUS_COLORS[a.status] || STATUS_COLORS.active;
              const busy = busyUid === a.user_id;
              return (
                <tr key={a.user_id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`demo-row-${a.user_id}`}>
                  <td className="px-3 py-2 font-medium text-gray-800">
                    {a.email}
                    {a.is_erma_auditor && (
                      <Badge className="ml-2 bg-purple-100 text-purple-700 border-purple-300 text-[10px]">ERMA</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{a.name}</td>
                  <td className="px-3 py-2 text-gray-600 capitalize">{a.role?.replace("_", " ")}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide border ${statusClass}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {a.last_login ? new Date(a.last_login).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button size="sm" variant="ghost" disabled={busy} className="h-7 text-xs" onClick={() => action("resend", a.user_id, "Resend")} data-testid={`demo-action-resend-${a.user_id}`}>
                        <Mail size={12} className="mr-1" /> Resend
                      </Button>
                      <Button size="sm" variant="ghost" disabled={busy} className="h-7 text-xs text-amber-700 hover:bg-amber-50" onClick={() => action("reset-password", a.user_id, "Reset PW")} data-testid={`demo-action-reset-${a.user_id}`}>
                        <KeyRound size={12} className="mr-1" /> Reset PW
                      </Button>
                      {a.status === "active" ? (
                        <>
                          <Button size="sm" variant="ghost" disabled={busy} className="h-7 text-xs text-amber-700 hover:bg-amber-50" onClick={() => action("disable", a.user_id, "Disable")} data-testid={`demo-action-disable-${a.user_id}`}>
                            <Ban size={12} className="mr-1" /> Disable
                          </Button>
                          <Button size="sm" variant="ghost" disabled={busy} className="h-7 text-xs text-rose-700 hover:bg-rose-50" onClick={() => action("revoke", a.user_id, "Revoke")} data-testid={`demo-action-revoke-${a.user_id}`}>
                            <Ban size={12} className="mr-1" /> Revoke
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" variant="ghost" disabled={busy} className="h-7 text-xs text-emerald-700 hover:bg-emerald-50" onClick={() => action("enable", a.user_id, "Enable")} data-testid={`demo-action-enable-${a.user_id}`}>
                          <Power size={12} className="mr-1" /> Enable
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Audit log */}
      <div>
        <h3 className="font-bold text-gray-700 flex items-center gap-2 mb-2 mt-4">
          <ClipboardList size={16} /> Recent demo-account audit log
        </h3>
        <div className="border border-gray-200 rounded-md max-h-72 overflow-y-auto" data-testid="demo-accounts-audit-log">
          {audit.length === 0 ? (
            <div className="p-3 text-sm text-gray-400">No audit rows yet.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-1.5 font-semibold text-gray-500">When</th>
                  <th className="text-left px-3 py-1.5 font-semibold text-gray-500">Actor</th>
                  <th className="text-left px-3 py-1.5 font-semibold text-gray-500">Action</th>
                  <th className="text-left px-3 py-1.5 font-semibold text-gray-500">Target</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((r) => (
                  <tr key={r.id} className="border-b border-gray-100">
                    <td className="px-3 py-1.5 text-gray-500">{new Date(r.timestamp).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-gray-700">{r.actor_email}</td>
                    <td className="px-3 py-1.5">
                      <span className="font-mono text-[11px] bg-gray-100 px-1.5 py-0.5 rounded">{r.action}</span>
                    </td>
                    <td className="px-3 py-1.5 text-gray-700">{r.target_email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoAccountsTab;
